import { useAbility } from '@casl/react';
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  lighten,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EntityIcon from 'src/components/Icons/EntityIcon';
import { useAdminUrlNavigation } from 'src/hooks/useAdminUrlNavigation';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import pages from 'src/router/routes';
import { useGetMeterPointsSummaryQuery } from 'src/services/switch-rest-api/meter-point.service';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';

import { formatNumber } from '../../../../../../switch-shared/src/utils/formatters';
import { ACTIONS } from '../../../../auth/utils/defineAbilityFor';
import { AbilityContext } from '../../../../context/canContext';
import { useDashboard } from '../../../../context/dashboardContext';
import { isAllSelection, isUtilityType } from '../../../../utils/selection';
import { useAllGroupsMetrics } from '../../hooks/useAllGroupsMetrics';

const MeterPointsWidget = () => {
  /* Hooks & Use Effects */

  const appGroupId = useEffectiveApplicationGroupId();
  const appId = useEffectiveApplicationId();

  const { utility, zone } = useDashboard();

  const theme = useTheme();
  const navigate = useNavigate();
  const { getApplicationScopedPath } = useAdminUrlNavigation();
  const ability = useAbility(AbilityContext);

  /* States */
  const isAllGroupsSelected = isAllSelection(appGroupId);

  /* All Groups Metrics Hook */
  const {
    metrics: allGroupsMetrics,
    isLoading: isAllGroupsLoading,
    isFetching: isAllGroupsFetching,
  } = useAllGroupsMetrics();

  /* API Calls */

  const { data, isLoading, isFetching } = useGetMeterPointsSummaryQuery(
    {
      ...(appId != null &&
        !isAllSelection(appId) && {
          applicationId: appId,
        }),
      ...(!isAllGroupsSelected && {
        applicationGroupId: appGroupId,
      }),
      ...(isUtilityType(utility) && { utilityType: utility }),
      zoneId: zone?._id,
    },
    {
      skip:
        !ability.can(ACTIONS.READ, 'MeterPoints') ||
        appGroupId == null ||
        isAllGroupsSelected,
      pollingInterval: 30_000,
    }
  );

  /* Functions & Functional Components */

  const getTooltipDescription = () => {
    if (isAllGroupsSelected) {
      return 'The number of bulk and non-bulk meter points across all groups';
    }
    if (zone?._id) {
      return `The number of bulk and non-bulk meter points in zone ${zone.name}`;
    }
    if (appId != null && !isAllSelection(appId)) {
      return 'The number of bulk and non-bulk meter points in the selected application';
    }
    return 'The number of bulk and non-bulk meter points in the selected group';
  };

  return (
    <WidgetCard
      title='Meter Points'
      subheader={
        zone?.name
          ? `Bulk and Non-Bulk in ${zone.name}`
          : 'Bulk and Non-Bulk counts'
      }
      tooltipDescription={getTooltipDescription()}
      avatar={<EntityIcon type='meterPoints' />}
      onClickFn={
        appId != null && !isAllSelection(appId)
          ? () =>
              navigate(
                getApplicationScopedPath(
                  pages.meterpoints.name,
                  pages.meterpoints.path
                )
              )
          : undefined
      }
      disabled={!ability.can(ACTIONS.READ, 'MeterPoints')}
      cardContentProps={{
        sx: {
          pt: `0 !important`,
          pb: `16px !important`,
          color: lighten(theme.palette.primary.main, 0.1),
        },
      }}
    >
      {isAllGroupsSelected ? (
        <>
          {isAllGroupsLoading || isAllGroupsFetching ? (
            <Grid
              height={'100%'}
              container
              alignContent={'center'}
              justifyContent={'center'}
            >
              <Grid item>
                <CircularProgress />
              </Grid>
            </Grid>
          ) : (
            <Grid
              container
              alignContent={'center'}
              justifyContent={'center'}
              height={'100%'}
              sx={{ mt: -1.1 }}
            >
              <Grid item display={'flex'} alignItems={'center'} gap={3}>
                <Box>
                  Bulk
                  <Typography
                    variant='h4'
                    fontSize={'25px'}
                    fontWeight={'bold'}
                  >
                    {formatNumber(allGroupsMetrics?.meterPoints.bulk ?? 0, 0)}
                  </Typography>
                </Box>
                <Box>
                  Non-Bulk
                  <Typography
                    variant='h4'
                    fontSize={'25px'}
                    fontWeight={'bold'}
                  >
                    {formatNumber(
                      allGroupsMetrics?.meterPoints.nonBulk ?? 0,
                      0
                    )}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </>
      ) : isLoading || isFetching ? (
        <Grid
          height={'100%'}
          container
          alignContent={'center'}
          justifyContent={'center'}
        >
          <Grid item>
            <CircularProgress />
          </Grid>
        </Grid>
      ) : (
        <>
          {data != null && (
            <Grid
              container
              alignContent={'center'}
              justifyContent={'center'}
              height={'100%'}
              sx={data.bulkCount + data.nonBulkCount !== 0 ? { mt: -1.1 } : {}}
            >
              {data.bulkCount + data.nonBulkCount === 0 ? (
                `No meter points found`
              ) : (
                <Grid item display={'flex'} alignItems={'center'} gap={3}>
                  <Box>
                    Bulk
                    <Typography
                      variant='h4'
                      fontSize={'25px'}
                      fontWeight={'bold'}
                    >
                      {formatNumber(data.bulkCount, 0)}
                    </Typography>
                  </Box>
                  <Box>
                    Non-Bulk
                    <Typography
                      variant='h4'
                      fontSize={'25px'}
                      fontWeight={'bold'}
                    >
                      {formatNumber(data.nonBulkCount, 0)}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </>
      )}
    </WidgetCard>
  );
};

export default MeterPointsWidget;
